<template lang="pug">
    .ma-sm-2
        v-spacer
        template(v-if="showLiveAutoAssistSection")
            v-container.mx-auto.text-center(v-if="isLoading")
                .text-center Loading auto-assistant data and transcript...
                v-progress-circular(
                    :size='100'
                    :width='10'
                    color='var(--volie-blue)'
                    indeterminate
                    style="margin-top: 100px;"
                )

            v-snackbar(
                v-model='snackbarOpen'
                :color='snackbar.color'
                :timeout="snackbar.timeout"
                top
            )
                | {{ snackbar.text }}

                template(v-slot:action="{ attrs }")
                    v-btn(dark text @click='snackbarOpen = false' v-bind="attrs")
                        | Close

            v-container.mx-auto(v-if="!isLoading && validAutoAssistData")
                GenericListenerAiData(
                    :auto-assist-data="autoAssistData"
                    :valid-auto-assist-data="validAutoAssistData"
                )
            v-alert(v-else-if="!isLoading && displayFailureMessage" outlined type='info' dense border='left' style="margin-top: 0px;")
                | Unable to retrieve AI Assistant call data.

            v-divider.divider-spacing(v-if="transferredFromAutoAssist")

        .text-center(v-if="isHistoryLoading")
            v-progress-circular(
                :size='100'
                :width='10'
                color='var(--volie-blue)'
                indeterminate
                style="margin-top: 100px;"
            )

        v-container.mx-auto(v-if="!isHistoryLoading")
            .text-subtitle-2.subtitle-2-padding.text-center
                | Auto Assistant Call History

            AutoAssistantCallHistoryDetails(
                v-if="validHistory"
                v-for='request, i in autoAssistantRequests'
                :key='i'
                :data="request"
            )

        v-alert(outlined type='warning' dense border='left' style="margin-top: 16px;" v-if="!isHistoryLoading && !validHistory")
            | No AI Assistant call history was found for this customer


</template>

<script lang="ts">
    import Vue from 'vue';

    import VolieState  from "@/store";

    // Components
    import GenericListenerAiData from '@/components/ai_assistants/generic_listener_ai_data.vue';
    import AutoAssistantCallHistoryDetails from '@/components/ai_assistants/call_history_details.vue';

    // Data
    import { GetAutoAssistantRequests } from "@/data";

    export default Vue.extend({
        data() {
            return {
                autoAssistantRequests: [],
                bus: VolieState.websocketClient().eventBus(),
                displayFailureMessage: false,
                firstRequestSent: false,
                isHistoryLoading: true,
                isLoading: true,
                snackbarOpen: false,
                snackbar: {
                    timeout: 20000,
                },
            }
        },
        methods: {
            showSnackbar(color, text) {
                if (color?.length > 0 && text?.length > 0) {
                    this.snackbar.color = color;
                    this.snackbar.text = text;
                    this.snackbarOpen = true;
                }
            },
            getAutoAssistantRequest() {

                // If the data has already been set, return
                if (this.validHistory) {
                    return;
                }

                if (!(this.customerId > 0)) {
                    this.isHistoryLoading = false;
                    return;
                }

                const options = {
                    customer_id: this.customerId,
                }

                this.isHistoryLoading = true;

                GetAutoAssistantRequests(options).then((response) => {
                    if (response?.Error === null && response.Data?.AutoAssistantRequests?.length > 0) {
                        this.autoAssistantRequests = response.Data.AutoAssistantRequests;
                        // remove the current request from history so it isn't shown twice in CC
                        if (this.autoAssistData?.id > 0) {
                            this.autoAssistantRequests = this.autoAssistantRequests.filter((request) => request.id !== this.autoAssistData.id);
                        }
                    }
                }).finally(() => {
                    this.isHistoryLoading = false;
                });
            },
        },
        computed: {
            fetchFailed() {
                return this.autoAssistData?.auto_assistant_type === 'error'
            },
            // Live transfer details are CC-only; admin customer profile is history-only.
            showLiveAutoAssistSection() {
                return this.inCommunicationCenter === true;
            },
            validAutoAssistData() {
                return this.autoAssistData?.auto_assistant_type != "" && this.autoAssistData?.event_body?.length > 0
            },
            validCampaignCustomer() {
                return this.currentCampaignCustomer?.campaign_customer_active_id > 0 && this.currentCampaignCustomer?.campaign_customer_id > 0;
            },
            validHistory() {
                return this.autoAssistantRequests?.length > 0;
            },
        },
        components: {
            AutoAssistantCallHistoryDetails,
            GenericListenerAiData,
        },
        props: {
            autoAssistData: Object,
            callId: Number,
            customerId: Number,
            currentCampaignCustomer: Object,
            inCommunicationCenter: Boolean,
            transferredFromAutoAssist: Boolean,
        },
        watch: {
            "autoAssistData"() {
                if (!this.showLiveAutoAssistSection) {
                    return;
                }
                if (this.validAutoAssistData) {
                    this.displayFailureMessage = false;
                    this.showSnackbar("green", "This call has been transferred from an AI assistant, you will find details of the call below.")
                } else if (this.fetchFailed) {
                    this.showSnackbar("red", "Unable to retrieve AI Assistant call data.")
                    this.displayFailureMessage = true;
                }
                this.isLoading = false;
            },
            customerId(newCustomerId, oldCustomerId) {
                // Refetch history when the customer changes (e.g. back-to-back AI calls)
                if (newCustomerId > 0 && newCustomerId !== oldCustomerId) {
                    this.autoAssistantRequests = [];
                    this.getAutoAssistantRequest();
                }
            },
        },
        created() {
            if (!this.showLiveAutoAssistSection) {
                this.isLoading = false;
            } else if (this.transferredFromAutoAssist) {
                if (this.validAutoAssistData) {
                    this.isLoading = false;
                    this.showSnackbar("green", "This call has been transferred from an AI assistant, you will find details of the call below.")
                } else if (this.fetchFailed) {
                    this.showSnackbar("red", "Unable to retrieve AI Assistant call data.")
                    this.isLoading = false;
                    this.displayFailureMessage = true;
                } else {
                    this.showSnackbar("green", "This call has been transferred from an AI assistant, call details are currently loading.")
                }
            } else {
                this.isLoading = false;
            }

            this.getAutoAssistantRequest();
        },
    });
</script>

<style lang="scss" scoped>
</style>
